import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const FleetStatusPanel = ({ servers, emergencyPause }) => {
  const [hoveredServer, setHoveredServer] = useState(null);

  const getPurposeColor = (purpose) => {
    switch (purpose) {
      case 'Production':
        return { bg: 'bg-blue-500', ring: 'ring-blue-400', text: 'text-blue-400', glow: 'shadow-blue-500/50' };
      case 'Canary':
        return { bg: 'bg-yellow-500', ring: 'ring-yellow-400', text: 'text-yellow-400', glow: 'shadow-yellow-500/50' };
      case 'Quarantine':
        return { bg: 'bg-orange-500', ring: 'ring-orange-400', text: 'text-orange-400', glow: 'shadow-orange-500/50' };
      case 'Sanitizer':
        return { bg: 'bg-red-500', ring: 'ring-red-400', text: 'text-red-400', glow: 'shadow-red-500/50' };
      default:
        return { bg: 'bg-slate-500', ring: 'ring-slate-400', text: 'text-slate-400', glow: 'shadow-slate-500/50' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Online':
        return 'CheckCircle2';
      case 'Warning':
        return 'AlertTriangle';
      case 'Quarantined':
        return 'ShieldAlert';
      case 'Offline':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const purposeCounts = servers?.reduce((acc, server) => {
    acc[server?.purpose] = (acc?.[server?.purpose] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden h-full">
      <div className="p-4 md:p-6 border-b border-slate-700">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-white">
            Fleet Status
          </h2>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${
              emergencyPause ? 'bg-red-500 animate-pulse' : 'bg-green-500 animate-pulse-subtle'
            }`} />
            <span className="text-sm text-slate-400">
              {emergencyPause ? 'Paused' : 'Active'}
            </span>
          </div>
        </div>
        <p className="text-sm text-slate-400">
          Real-time server fleet visualization
        </p>
      </div>

      <div className="p-6">
        <div className="flex flex-wrap gap-4 mb-6">
          {Object.entries(purposeCounts)?.map(([purpose, count]) => {
            const colors = getPurposeColor(purpose);
            return (
              <div key={purpose} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${colors?.bg}`} />
                <span className="text-sm text-slate-300">
                  {purpose} <span className="text-slate-500">({count})</span>
                </span>
              </div>
            );
          })}
        </div>

        <div className="relative bg-slate-950 rounded-lg p-8 min-h-[400px] border border-slate-800">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {servers?.map((server, index) => {
              const colors = getPurposeColor(server?.purpose);
              const isHovered = hoveredServer === server?.id;
              
              return (
                <Link
                  key={server?.id}
                  to={`/server-detail?id=${server?.id}`}
                  className="relative group"
                  onMouseEnter={() => setHoveredServer(server?.id)}
                  onMouseLeave={() => setHoveredServer(null)}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`relative w-16 h-16 rounded-full ${colors?.bg} flex items-center justify-center transition-all duration-300 ${
                        isHovered ? `ring-4 ${colors?.ring} scale-110 shadow-lg ${colors?.glow}` : 'ring-2 ring-slate-700'
                      } ${
                        server?.status === 'Online' ? 'animate-pulse-subtle' : ''
                      } ${
                        emergencyPause ? 'opacity-50' : ''
                      }`}
                    >
                      <Icon name="Server" size={24} className="text-white" />
                      <div
                        className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                          server?.status === 'Online' ? 'bg-green-500' :
                          server?.status === 'Warning' ? 'bg-yellow-500' :
                          server?.status === 'Quarantined' ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                      >
                        <Icon name={getStatusIcon(server?.status)} size={12} className="text-white" />
                      </div>
                    </div>
                    
                    <div className="mt-3 text-center">
                      <div className={`text-sm font-medium ${colors?.text} mb-1`}>
                        {server?.name}
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        {server?.ip}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Rep: {server?.reputation}
                      </div>
                    </div>

                    {isHovered && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-10 bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl min-w-[200px]">
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Purpose:</span>
                            <span className={`font-medium ${colors?.text}`}>{server?.purpose}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Status:</span>
                            <span className="text-white">{server?.status}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Reputation:</span>
                            <span className="text-white">{server?.reputation}/100</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetStatusPanel;